import { useEffect, useState } from 'react';

// Same install-prompt pattern as the worker app — captures Chrome's
// `beforeinstallprompt`, falls back to an iOS hint for Safari.

type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  return (window.navigator as unknown as { standalone?: boolean }).standalone === true;
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function InstallPrompt() {
  const [evt, setEvt] = useState<BIPEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    if (installed) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setEvt(e as BIPEvent);
    };
    const onInstalled = () => setInstalled(true);

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    if (isIos() && !isStandalone()) {
      const t = setTimeout(() => {
        if (!sessionStorage.getItem('hideIosInstall')) setShowIosHint(true);
      }, 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [installed]);

  if (installed) return null;

  if (evt) {
    return (
      <div className="fixed bottom-3 left-3 right-3 z-50 sm:left-auto sm:right-3 sm:max-w-sm">
        <div className="flex items-center gap-3 rounded-2xl bg-[#0F2D1F] px-4 py-3 text-white shadow-xl">
          <div className="flex-1 text-sm">
            <div className="font-semibold">Install FixItNinjaz Admin</div>
            <div className="text-white/80 text-xs">Add to home screen / desktop.</div>
          </div>
          <button
            onClick={async () => {
              await evt.prompt();
              const choice = await evt.userChoice;
              if (choice.outcome === 'accepted') setInstalled(true);
              setEvt(null);
            }}
            className="rounded-lg bg-[#F97316] px-3 py-2 text-sm font-semibold text-white"
          >
            Install
          </button>
          <button
            onClick={() => setEvt(null)}
            aria-label="Dismiss"
            className="text-white/70 px-1 text-xl leading-none"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div className="fixed bottom-3 left-3 right-3 z-50 pointer-events-none">
        <div className="rounded-2xl bg-white px-4 py-3 text-stone-900 shadow-2xl ring-1 ring-stone-200 pointer-events-auto">
          <div className="flex items-start gap-3">
            <div className="flex-1 text-sm">
              <div className="font-semibold text-[#0F2D1F]">Install FixItNinjaz Admin</div>
              <ol className="mt-1 space-y-1 text-stone-700 text-xs leading-relaxed list-decimal list-inside">
                <li>
                  Tap the Share button{' '}
                  <span aria-hidden className="inline-block align-middle">
                    <svg width="14" height="18" viewBox="0 0 14 18" fill="none" className="inline">
                      <path d="M7 1v11M3 5l4-4 4 4" stroke="#0F2D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="1" y="7" width="12" height="10" rx="2" stroke="#0F2D1F" strokeWidth="1.5"/>
                    </svg>
                  </span>{' '}
                  in Safari's toolbar
                </li>
                <li>Scroll and tap <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong></li>
              </ol>
              <div className="mt-2 text-[11px] text-stone-500">
                ↓ The Share button is at the bottom of Safari
              </div>
            </div>
            <button
              onClick={() => {
                sessionStorage.setItem('hideIosInstall', '1');
                setShowIosHint(false);
              }}
              aria-label="Dismiss"
              className="text-stone-400 px-1 text-xl leading-none -mt-1"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
